import {
  CraSegmentType,
  CraDateSegmentsSchema,
  CraNumberSegmentsSchema,
  CraStringSegmentsSchema,
  CraVariableSchema,
  CraApiVariableSchema,
} from '../../../domain/cra/CraDomains';
import ValueFactory from '../../../util/ValueFactory';
import React, {ChangeEvent, useEffect, useRef, useState, useMemo} from 'react';
import {cloneDeep} from 'lodash';
import GeneralConfigurationItemContainer from '../../../components/investigation/configuration/detail/general/GeneralConfigurationItemContainer';
import LanguageUtil from '../../../util/LanguageUtil';
import FormicaInput from '../../../components/custom/FormicaInput';
import RoleHelper from '../../../RoleHelper';
import ComponentName from '../../../constant/ComponentName';
import {Dialog} from 'primereact/dialog';
import {AnimatedButton} from '@trlogic/ui-pack';
import CraPolicyListComponent from './CraPolicyListComponent';
import CraService from '../../../service/CraService';
import ServiceContainer from '../../../config/ServiceContainer';
import {ServiceNames} from '../../../constant/ServiceNames';
import {CraType} from '../../../constant/Constants';
import {generateUUIDV4} from '../../../util/Generator';
import CustomTabv2 from 'components/custom/CustomTabv2';
import FormicaSearchSelectBox, {FormicaSelectBoxItem} from 'components/custom/FormicaSearchSelectBox';
import HttpMethod from 'constant/HttpMethod';
import ParametersComponent from './api/ParametersComponent';
import AuthorizationComponent from './api/AuthorizationComponent';
import HeadersComponent from './api/HeadersComponent';
import BodyComponent from './api/BodyComponent';
import {useSelector, useDispatch} from 'react-redux';
import {RootState} from 'redux/store';
import {setCraApiVariable, clearCraApiVariable} from 'redux/actions/CraApiVariableActions';
import {changePolicy} from 'redux/actions/PolicyActions';
import {ApiVariableParameter} from 'domain/enrichment/EnrichmentDomain';
import MappingComponent from './api/MappingComponent';
import FormicaLabel from 'components/custom/FormicaLabel';
import DataTypeSelectBoxComponent from 'components/custom/DataTypeSelectBoxComponent';
import {ResponseFormats} from 'constant/ResponseFormats';
import DataType from 'constant/DataType';
import {ListComponent} from './watchlist/ListComponent';
import MessageUtil from '../../../util/MessageUtil';

interface NewSegment {
  key?: string | undefined;
  value?: string | number | undefined;
  description?: string | undefined;
  from?: string | number | undefined;
  to?: string | number | undefined;
  score?: number | undefined;
}

export function CraPolicyEditorModal({
                                       onClose,
                                       visible = false,
                                       variable = ValueFactory.initializeCraVariableSchema(CraSegmentType.STRING),
                                       assessor,
                                     }: CraPolicyEditorModalProps) {
  const craService: CraService = ServiceContainer.getInstance().get(ServiceNames.CRA);
  const {craApiVariables} = useSelector((state: RootState) => state.craApiVariable);
  const {policy, isUpdate} = useSelector((state: RootState) => state.policy);
  const lastCategory = useSelector((state: RootState) => state.page.lastCategory);
  const dispatch = useDispatch();
  const name = variable.name;
  const currentVariable = useMemo(() => {
    return craApiVariables[name] || ValueFactory.initializeCraApiVariableSchema();
  }, [craApiVariables, name]);

  const [dialogVisible, setDialogVisible] = useState<boolean>(visible);
  const [craVariable, setCraVariable] = useState<CraVariableSchema>(variable);
  const [addButtonDisabled, setAddButtonDisabled] = useState<boolean>(false);
  const [newSegment, setNewSegment] = useState<NewSegment>({});
  const [pendingSegments, setPendingSegments] = useState<
    (CraStringSegmentsSchema | CraNumberSegmentsSchema | CraDateSegmentsSchema)[]
  >([]);

  const dialogRef = useRef<Dialog>(null);
  const skipCancelOnHideRef = useRef<boolean>(false);
  const apiSnapshotRef = useRef<CraApiVariableSchema | undefined>(undefined);

  useEffect(() => {
    setDialogVisible(visible);
  }, [visible]);

  useEffect(() => {
    setCraVariable(variable);
  }, [variable]);

  useEffect(() => {
    if (!visible) {
      return;
    }
    skipCancelOnHideRef.current = false;
    if (variable.type !== CraSegmentType.API) {
      apiSnapshotRef.current = undefined;
      return;
    }
    const existing = craApiVariables[name];
    if (!existing) {
      dispatch(setCraApiVariable(name, ValueFactory.initializeCraApiVariableSchema()));
    }
    apiSnapshotRef.current = cloneDeep(existing || ValueFactory.initializeCraApiVariableSchema());
  }, [visible, name, variable.type]);

  const onChange = (key: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewSegment((prev) => ({
      ...prev,
      [key]: e.target.value === '' ? undefined : e.target.value,
    }));
  };

  const onChangeUrl = (e: ChangeEvent<HTMLInputElement>) => {
    const oldUrl = getKeyAndValues(currentVariable.url);
    currentVariable.url = e.currentTarget.value;
    const url = getKeyAndValues(currentVariable.url);

    oldUrl.map((item, index) => {
      if (item != url[index]) {
        currentVariable.parameters = currentVariable.parameters?.filter((param) => param.key != item) || [];
      }
    });
    dispatch(setCraApiVariable(name, currentVariable));
    addNewParameter(url);
  };

  const getKeyAndValues = (url: string) => {
    const regex = /[?&]([^=#]+)=([^&#]*)/g;
    let urlParams = url.split(regex);
    urlParams = urlParams.filter((item) => item != '');
    urlParams.shift();
    return urlParams;
  };

  const getUniqueListBy = (arr: any[], key: string) => {
    return [...new Map(arr.map((item) => [item[key], item])).values()];
  };

  const addNewParameter = (url: string[]) => {
    for (let i = 0; i < url.length; i++) {
      if (url.length % 2 == 0) {
        const parameter: ApiVariableParameter = {
          key: url[i],
          value: url[i + 1],
          description: '',
          isActive: true,
        };
        currentVariable.parameters = getUniqueListBy([...(currentVariable.parameters || []), parameter], 'key');
        i++;
      }
    }
    dispatch(setCraApiVariable(name, currentVariable));
  };

  const compareVariables = (a: CraVariableSchema, b: CraVariableSchema) => {
    const {mapping: _mappingA, ...restA} = a;
    const {mapping: _mappingB, ...restB} = b;
    return JSON.stringify(restA) === JSON.stringify(restB);
  };

  const closeModal = () => {
    setPendingSegments([]);
    setNewSegment({});
    setDialogVisible(false);
    onClose && onClose();
  };

  const restoreApiSnapshot = () => {
    if (variable.type !== CraSegmentType.API || !apiSnapshotRef.current) {
      dispatch(clearCraApiVariable(name));
      return;
    }
    dispatch(setCraApiVariable(name, cloneDeep(apiSnapshotRef.current)));
  };

  const onCancel = () => {
    if (!visible) {
      return;
    }
    if (skipCancelOnHideRef.current) {
      skipCancelOnHideRef.current = false;
      return;
    }
    restoreApiSnapshot();
    setCraVariable(variable);
    skipCancelOnHideRef.current = true;
    closeModal();
  };

  const onSave = async () => {
    if (!visible) {
      return;
    }
    if (craVariable.weight == undefined || isNaN(craVariable.weight) || craVariable.weight < 0 || craVariable.weight > 100) {
      MessageUtil.error({
        summary: LanguageUtil.get({id: 'common.label.segment', defaultMessage: 'CRA Segment'}),
        detail: LanguageUtil.get({id: 'pages.cra.modal.weight', defaultMessage: 'Weight must be between 0 and 100.'})
      });
      return;
    }

    if (craVariable.type === CraSegmentType.API) {
      const methodValid = typeof currentVariable.method === 'string' && currentVariable.method.trim().length > 0;
      const urlValid = typeof currentVariable.url === 'string' && currentVariable.url.trim().length > 0;
      const mappingValid = currentVariable.mapping?.children && currentVariable.mapping.children.length > 1;
      if (!urlValid || !mappingValid || !methodValid) {
        MessageUtil.error({
          summary: LanguageUtil.get({id: 'common.label.cra.variable', defaultMessage: 'CRA Variable'}),
          detail: LanguageUtil.get({
            id: 'pages.cra.modal.apiRequired',
            defaultMessage: 'Method, URL and mapping are required.',
          }),
        });
        return;
      }
    }

    if (pendingSegments.length > 0) {
      for (const segment of pendingSegments) {
        if (!validateSegment(segment, craVariable.type as CraSegmentType)) {
          return;
        }
        await craService.deleteSegment(segment.id, craVariable.type as CraSegmentType, false);
        await craService.segment(segment, craVariable.type as CraSegmentType, false);
      }
      setPendingSegments([]);
    }

    const variableToSave: CraVariableSchema = {
      ...craVariable,
      ...(craVariable.type === CraSegmentType.API ? currentVariable : {}),
      weight: Number(craVariable.weight),
      isEnabled: craVariable.isEnabled ?? false,
    };

    let isSaved = true;
    switch (craVariable.type) {
      case CraSegmentType.API: {
        const updatedPolicy = {
          ...policy,
          variables: policy.variables.map((item) =>
            item.name === name ? {...item, ...variableToSave} : item,
          ),
        };
        dispatch(changePolicy(updatedPolicy));
        isSaved = await craService.save(updatedPolicy, isUpdate, lastCategory);
        break;
      }
      case CraSegmentType.STRING:
      case CraSegmentType.NUMBER:
      case CraSegmentType.DATE:
      case CraSegmentType.WATCHLIST:
        if (!compareVariables(variableToSave, variable)) {
          isSaved = await craService.variable(variableToSave, assessor, true);
        }
        break;
    }
    if (!isSaved) {
      return;
    }

    if (variable.type !== CraSegmentType.API) {
      dispatch(clearCraApiVariable(name));
    }

    skipCancelOnHideRef.current = true;
    closeModal();
  };

  const getFooter = () => {
    const canEdit = RoleHelper.canEdit('Cra');
    return (
      <div className={'flex w-full items-center justify-end gap-x-3 px-2'}>
        <AnimatedButton
          customType={'secondary'}
          customSize={'sm'}
          icon={'multiplier'}
          className={'min-w-[120px] w-full'}
          text={LanguageUtil.get({id: 'common.button.cancel', defaultMessage: 'Cancel'})}
          onClick={onCancel}
          data-testid={`${ComponentName.POLICY}_settings_cancel_button`}
        />
        <AnimatedButton
          customType={'primary'}
          customSize={'sm'}
          className={'min-w-[120px] w-full'}
          icon={'save'}
          text={LanguageUtil.get({id: 'common.button.save', defaultMessage: 'Save'})}
          onClick={onSave}
          disabled={!canEdit}
          data-testid={`${ComponentName.POLICY}_settings_save_button`}
        />
      </div>
    );
  };

  const validateSegment = (segment: (CraStringSegmentsSchema | CraNumberSegmentsSchema | CraDateSegmentsSchema | NewSegment), type: CraSegmentType) => {
    let _segment;

    switch (type) {
      case CraSegmentType.STRING:
        _segment = segment as CraStringSegmentsSchema;
        if (!_segment.value || _segment.value === '') {
          MessageUtil.error({
            summary: LanguageUtil.get({id: 'common.label.cra.segment', defaultMessage: 'CRA Segment'}),
            detail: LanguageUtil.get({id: 'craPolicyEditorModal.error.valueRequired', defaultMessage: 'Value field is required.'})
          });
          return false;
        }
        if (_segment.score === undefined || isNaN(Number(_segment.score))) {
          MessageUtil.error({
            summary: LanguageUtil.get({id: 'common.label.cra.segment', defaultMessage: 'CRA Segment'}),
            detail: LanguageUtil.get({
              id: 'craPolicyEditorModal.error.scoreRequired',
              defaultMessage: 'Score field is required and must be a number.'
            })
          });
          return false;
        }
        break;
      case CraSegmentType.NUMBER:
      case CraSegmentType.DATE:
        _segment = segment as CraNumberSegmentsSchema;
        if (_segment.from === undefined || typeof _segment.from == "string" && _segment.from === '' || isNaN(Number(_segment.from))) {
          MessageUtil.error({
            summary: LanguageUtil.get({id: 'common.label.cra.segment', defaultMessage: 'CRA Segment'}),
            detail: LanguageUtil.get({id: 'craPolicyEditorModal.error.fromRequired', defaultMessage: 'From field is required and must be a number.'})
          });
          return false;
        }
        if (_segment.to === undefined || typeof _segment.to == "string" && _segment.to === '' || isNaN(Number(_segment.to))) {
          MessageUtil.error({
            summary: LanguageUtil.get({id: 'common.label.cra.segment', defaultMessage: 'CRA Segment'}),
            detail: LanguageUtil.get({id: 'craPolicyEditorModal.error.toRequired', defaultMessage: 'To field is required and must be a number.'})
          });
          return false;
        }
        if (_segment.score === undefined || isNaN(Number(_segment.score))) {
          MessageUtil.error({
            summary: LanguageUtil.get({id: 'common.label.cra.segment', defaultMessage: 'CRA Segment'}),
            detail: LanguageUtil.get({
              id: 'craPolicyEditorModal.error.scoreRequired',
              defaultMessage: 'Score field is required and must be a number.'
            })
          });
          return false;
        }
        break;
    }

    const scoreNum = Number(segment.score);
    if (scoreNum < 0 || scoreNum > 100) {
      MessageUtil.error({
        summary: LanguageUtil.get({id: 'common.label.cra.segment', defaultMessage: 'CRA Segment'}),
        detail: LanguageUtil.get({id: 'pages.cra.modal.scoreRange', defaultMessage: 'Score must be between 0 and 100.'})
      });
      return false;
    }

    return true;

  }

  const onClickAdd = async () => {
    if (addButtonDisabled) return;

    const scoreNum = Number(newSegment.score);
    const type = craVariable.type as CraSegmentType;

    const isValid: boolean = validateSegment(newSegment, type);
    if (!isValid) return;

    setAddButtonDisabled(true);

    const variableData = {
      id: generateUUIDV4(),
      ...newSegment,
      variable: craVariable.name,
      assessor,
      score: scoreNum,
    };
    await craService.segment(variableData as CraStringSegmentsSchema | CraNumberSegmentsSchema | CraDateSegmentsSchema, craVariable.type as CraSegmentType);
    setNewSegment({});
    setAddButtonDisabled(false);
  };

  const onChangeMethod = (e: FormicaSelectBoxItem) => {
    currentVariable.method = e.value as HttpMethod;
    dispatch(setCraApiVariable(name, currentVariable));
  };

  const onChangeDataType = (e: FormicaSelectBoxItem) => {
    currentVariable.properties = e.properties || {dataType: DataType.STRING};
    dispatch(setCraApiVariable(name, currentVariable));
  };

  const onChangeResponseType = (e: FormicaSelectBoxItem) => {
    currentVariable.responseType = e.value as ResponseFormats;
    dispatch(setCraApiVariable(name, currentVariable));
  };

  const onChangeTimeout = (e: ChangeEvent<HTMLInputElement>) => {
    currentVariable.timeout = parseInt(e.target.value) || 1;
    dispatch(setCraApiVariable(name, currentVariable));
  };

  const getResponseFormats = () => {
    return [
      {
        label: LanguageUtil.get({id: 'common.label.json', defaultMessage: 'JSON'}),
        value: ResponseFormats.JSON,
      },
      {
        label: LanguageUtil.get({id: 'common.label.xml', defaultMessage: 'XML'}),
        value: ResponseFormats.XML,
      },
      {
        label: LanguageUtil.get({id: 'common.label.text', defaultMessage: 'Text'}),
        value: ResponseFormats.TEXT,
      },
    ];
  };

  const onDeleteSegment = async (id: string) => {
    await craService.deleteSegment(id, craVariable.type as CraSegmentType);
  };

  const onChangeSegment = (segment: CraStringSegmentsSchema | CraNumberSegmentsSchema | CraDateSegmentsSchema, segmentType: CraSegmentType) => {
    setPendingSegments(prev => {
      const filtered = prev.filter(s => s.id !== segment.id);
      return [...filtered, segment];
    });
  }

  const getMethodTypes = () => {
    return [
      {
        label: 'GET',
        value: HttpMethod.GET,
      },
      {
        label: 'POST',
        value: HttpMethod.POST,
      },
      {
        label: 'PUT',
        value: HttpMethod.PUT,
      },
      {
        label: 'DELETE',
        value: HttpMethod.DELETE,
      },
    ];
  };

  const getTabs = () => {
    return [
      {
        key: 'Parameters',
        label: LanguageUtil.get({id: 'common.tabs.parameters', defaultMessage: 'Parameters'}),
        content: <ParametersComponent name={name}/>,
        testid: `${ComponentName.POLICY}_parameters_tab`,
      },
      {
        key: 'Authorization',
        label: LanguageUtil.get({id: 'common.tabs.authorization', defaultMessage: 'Authorization'}),
        content: <AuthorizationComponent name={name}/>,
        testid: `${ComponentName.POLICY}_auth_tab`,
      },
      {
        key: 'Headers',
        label: LanguageUtil.get({id: 'common.tabs.headers', defaultMessage: 'Headers'}),
        content: <HeadersComponent name={name}/>,
        testid: `${ComponentName.POLICY}_headers_tab`,
      },
      {
        key: 'Body',
        label: LanguageUtil.get({id: 'common.tabs.body', defaultMessage: 'Body'}),
        content: <BodyComponent name={name}/>,
        testid: `${ComponentName.POLICY}_body_tab`,
      },
      {
        key: 'Mapping',
        label: LanguageUtil.get({id: 'common.tabs.mapping', defaultMessage: 'Mapping'}),
        content: <MappingComponent variableName={name}/>,
        testid: `${ComponentName.POLICY}_mapping_tab`,
      },
    ];
  };

  const getInputs = () => {
    const dataType = craVariable.type as CraSegmentType;
    switch (dataType) {
      case CraSegmentType.STRING:
        return (
          <>
            <FormicaInput
              className="h-[34px]"
              disabled={!RoleHelper.canEdit('Cra')}
              placeholder={LanguageUtil.get({id: 'common.label.value', defaultMessage: 'Value'})}
              value={newSegment.value ?? ''}
              onChange={onChange('value')}
              data-testid={`${ComponentName.POLICY}_value_input`}
            />
            <FormicaInput
              className="h-[34px]"
              disabled={!RoleHelper.canEdit('Cra')}
              placeholder={LanguageUtil.get({id: 'common.label.score', defaultMessage: 'Score'})}
              type="number"
              inputMode="numeric"
              value={newSegment.score ?? ''}
              onChange={onChange('score')}
              data-testid={`${ComponentName.POLICY}_score_input`}
            />
            <div>
              <AnimatedButton
                onClick={onClickAdd}
                icon={'add'}
                customType={'primary'}
                customSize={'sm'}
                disabled={addButtonDisabled}
                text={LanguageUtil.get({id: 'common.button.add', defaultMessage: 'Add'})}
              />
            </div>
          </>
        );
      case CraSegmentType.NUMBER:
      case CraSegmentType.DATE:
        return (
          <>
            <FormicaInput
              className="h-[34px]"
              disabled={!RoleHelper.canEdit('Cra')}
              placeholder={LanguageUtil.get({id: 'common.label.from', defaultMessage: 'From'})}
              type={'number'}
              value={newSegment.from ?? ''}
              onChange={onChange('from')}
              data-testid={`${ComponentName.POLICY}_from_input`}
            />
            <FormicaInput
              className="h-[34px]"
              disabled={!RoleHelper.canEdit('Cra')}
              placeholder={LanguageUtil.get({id: 'common.label.to', defaultMessage: 'To'})}
              type={'number'}
              value={newSegment.to ?? ''}
              onChange={onChange('to')}
              data-testid={`${ComponentName.POLICY}_to_input`}
            />
            <FormicaInput
              className="h-[34px]"
              disabled={!RoleHelper.canEdit('Cra')}
              placeholder={LanguageUtil.get({id: 'common.label.score', defaultMessage: 'Score'})}
              type="number"
              inputMode="numeric"
              value={newSegment.score ?? ''}
              onChange={onChange('score')}
              required
              data-testid={`${ComponentName.POLICY}_score_input`}
            />
            <div>
              <AnimatedButton
                onClick={onClickAdd}
                icon={'add'}
                customType={'primary'}
                customSize={'sm'}
                disabled={addButtonDisabled}
                text={LanguageUtil.get({id: 'common.button.add', defaultMessage: 'Add'})}
              />
            </div>
          </>
        );
      case CraSegmentType.API:
        return (
          <div className="w-full">
            <div className="flex gap-x-2 w-full items-center h-9 mb-5">
              <div className="flex w-[120px]">
                <FormicaSearchSelectBox
                  placeholder={LanguageUtil.get({id: 'common.input.placeholder.method', defaultMessage: 'Method'})}
                  value={currentVariable.method}
                  options={getMethodTypes()}
                  rounded
                  onChange={(e: FormicaSelectBoxItem) => onChangeMethod(e)}
                  data-testid={`${ComponentName.POLICY}_method_select`}
                />
              </div>
              <FormicaInput
                disabled={!RoleHelper.canEdit('EnrichmentApi')}
                value={currentVariable.url}
                onChange={(e: ChangeEvent<HTMLInputElement>) => onChangeUrl(e)}
                placeholder={LanguageUtil.get({id: 'common.input.placeholder.url', defaultMessage: 'URL'})}
                data-testid={`${ComponentName.POLICY}_url_input`}
              />
            </div>

            <div className="flex pb-5 w-full h-full flex-col md:flex-row">
              <div className="w-full md:w-1/5 flex flex-col md:border-r border-f-black-5 pr-5 gap-y-3">
                <div className={'flex flex-col'}>
                  <FormicaLabel label={LanguageUtil.get({id: 'common.input.placeholder.dataType', defaultMessage: 'Data Type'})}/>
                  <DataTypeSelectBoxComponent
                    disabled={!RoleHelper.canEdit('EnrichmentApi')}
                    properties={currentVariable.properties}
                    onChangeValue={(e) => onChangeDataType(e)}
                    data-testid={`${ComponentName.API_VARIABLE}_data_type_select`}
                  />
                </div>
                <div className={'flex flex-col'}>
                  <FormicaLabel label={LanguageUtil.get({id: 'common.label.responseFormat', defaultMessage: 'Response Format'})}/>
                  <FormicaSearchSelectBox
                    options={getResponseFormats()}
                    onChange={(e: FormicaSelectBoxItem) => onChangeResponseType(e)}
                    value={currentVariable.responseType}
                    rounded={true}
                    disabled={!RoleHelper.canEdit('EnrichmentApi')}
                    placeholder={LanguageUtil.get({id: 'common.input.placeholder.selectResponseFormat', defaultMessage: 'Select a response format'})}
                    data-testid={`${ComponentName.API_VARIABLE}_response_format_select`}
                  />
                </div>
                <div className={'flex flex-col'}>
                  <FormicaLabel label={LanguageUtil.get({id: 'common.label.timeout', defaultMessage: 'Timeout (ms)'})}/>
                  <FormicaInput
                    disabled={!RoleHelper.canEdit('EnrichmentApi')}
                    type={'number'}
                    min={1}
                    placeholder={LanguageUtil.get({id: 'common.input.placeholder.timeout', defaultMessage: 'Timeout (ms)'})}
                    value={currentVariable.timeout}
                    onChange={onChangeTimeout}
                    data-testid={`${ComponentName.API_VARIABLE}_timeout_input`}
                  />
                </div>
              </div>
              <div className="w-full md:w-4/5 h-full pl-5 gap-y-4">
                <CustomTabv2 isContainer={false} tab={getTabs()}/>
              </div>
            </div>
          </div>
        );
      case CraSegmentType.WATCHLIST:
      default:
        return null;
    }
  };

  const getComponents = () => {
    const dataType = craVariable.type as CraSegmentType;

    switch (dataType) {
      case CraSegmentType.STRING:
      case CraSegmentType.NUMBER:
      case CraSegmentType.DATE:
        return <CraPolicyListComponent variable={craVariable} onDelete={onDeleteSegment} onChange={onChangeSegment}/>;
      case CraSegmentType.WATCHLIST:
        return <ListComponent selectedWatchlistType={craVariable.watchlistType} variable={craVariable} setVariable={setCraVariable}/>;
      default:
        return null;
    }
  };

  const onChangeWeight = (e: React.ChangeEvent<HTMLInputElement>) => {
    const num = e.target.value ? parseFloat(e.target.value) : undefined

    if (num != undefined && (num < 0 || num > 100)) {
      MessageUtil.error({
        summary: LanguageUtil.get({id: 'common.label.cra.segment', defaultMessage: 'CRA Segment'}),
        detail: LanguageUtil.get({id: 'pages.cra.modal.weight', defaultMessage: 'Weight must be between 0 and 100.'})
      });
      return;
    }
    setCraVariable({
      ...craVariable,
      weight: num,
    } as CraVariableSchema);
  };
  return (
    <>
      <Dialog ref={dialogRef} onHide={onCancel} visible={dialogVisible} className={'w-3/4 !h-full flex flex-col'}
        contentClassName={'flex flex-col flex-1 min-h-0 overflow-hidden'}
        contentStyle={{display: 'flex', flexDirection: 'column', overflow: 'hidden', flex: '1 1 0', minHeight: 0}}
        footer={getFooter()}
      >
        <div className={'shrink-0'}>
        <GeneralConfigurationItemContainer
          header={LanguageUtil.get({id: 'common.label.weight', defaultMessage: 'Weight'})}
          description={LanguageUtil.get({
            id: 'pages.cra.modal.message',
            defaultMessage: 'Select the transaction field that will provider.',
          })}
        >
          <span className={'flex w-full'}>
            <FormicaInput
              className={'h-[34px]'}
              disabled={!RoleHelper.canEdit('Cra')}
              placeholder={LanguageUtil.get({id: 'common.label.weight', defaultMessage: 'Weight'})}
              type="number"
              inputMode="numeric"
              value={craVariable.weight}
              onChange={onChangeWeight}
              data-testid={`${ComponentName.POLICY}_weight_input`}
            />
          </span>
        </GeneralConfigurationItemContainer>
        <div className={'flex w-full gap-x-5 flex-row pt-5'}>{getInputs()}</div>
        </div>
        <div className={'flex-1 min-h-0 overflow-hidden pt-3'}>{getComponents()}</div>
      </Dialog>
    </>
  );
}

export interface CraPolicyEditorModalProps {
  variable?: CraVariableSchema;
  assessor: CraType;
  visible?: boolean;
  onClose?: () => void;
  policyId?: string;
}
